import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import api from "@/lib/api";

interface Announcement {
  id: number;
  message: string;
  link_url: string | null;
  link_text: string | null;
  background_color: string | null;
  text_color: string | null;
  is_active: boolean;
  display_order: number;
}

export default function AnnouncementBar() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get("/api/v1/announcements");
      const data = response.data;
      
      // Handle different response structures
      let announcementsList: Announcement[] = [];
      
      if (data) {
        // Check if wrapped in success response
        if (data.data && Array.isArray(data.data.announcements)) {
          announcementsList = data.data.announcements;
        } else if (data.data && Array.isArray(data.data)) {
          announcementsList = data.data;
        } else if (Array.isArray(data.announcements)) {
          announcementsList = data.announcements;
        } else if (Array.isArray(data)) {
          announcementsList = data;
        }
      }
      
      setAnnouncements(announcementsList);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      setAnnouncements([]); // Ensure it's always an array
    }
  };

  // Filter out dismissed announcements
  const visibleAnnouncements = Array.isArray(announcements)
    ? announcements.filter((announcement) => !dismissedIds.has(announcement.id))
    : [];

  const currentAnnouncement = visibleAnnouncements[currentIndex];

  const handleDismiss = (id: number) => {
    setDismissedIds((prev) => new Set([...prev, id]));
    // Move to next announcement if available
    if (currentIndex < visibleAnnouncements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  // Auto-rotate announcements if multiple exist
  useEffect(() => {
    if (visibleAnnouncements.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % visibleAnnouncements.length);
      }, 5000); // Rotate every 5 seconds
      return () => clearInterval(interval);
    }
  }, [visibleAnnouncements.length]);

  if (!currentAnnouncement || visibleAnnouncements.length === 0) {
    return null;
  }

  // Parse colors - support both hex and Tailwind classes
  const bgColor = currentAnnouncement.background_color || "#000000";
  const textColor = currentAnnouncement.text_color || "#ffffff";

  // Check if color is a Tailwind class or hex
  const isTailwindClass = bgColor.startsWith("bg-") || textColor.startsWith("text-");
  const bgStyle = isTailwindClass ? {} : { backgroundColor: bgColor };
  const textStyle = isTailwindClass ? {} : { color: textColor };
  const bgClass = isTailwindClass ? bgColor : "";
  const textClass = isTailwindClass ? textColor : "";

  const content = (
    <div
      className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium ${
        isTailwindClass ? `${bgClass} ${textClass}` : ""
      }`}
      style={!isTailwindClass ? { ...bgStyle, ...textStyle } : {}}
    >
      <span className="text-center flex-1">{currentAnnouncement.message}</span>
      {currentAnnouncement.link_url && currentAnnouncement.link_text && (
        <Link
          to={currentAnnouncement.link_url}
          className="underline font-semibold hover:opacity-80 transition-opacity"
          style={!isTailwindClass ? textStyle : {}}
        >
          {currentAnnouncement.link_text}
        </Link>
      )}
      <button
        onClick={() => handleDismiss(currentAnnouncement.id)}
        className="ml-2 hover:opacity-70 transition-opacity"
        aria-label="Dismiss announcement"
        style={!isTailwindClass ? textStyle : {}}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={currentAnnouncement.id}
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed top-0 left-0 right-0 z-[60]"
        data-announcement-bar
      >
        {content}
        {/* Indicator dots if multiple announcements */}
        {visibleAnnouncements.length > 1 && (
          <div className="flex justify-center gap-1 py-1 bg-gray-100">
            {visibleAnnouncements.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentIndex ? "bg-gray-600 w-4" : "bg-gray-300"
                }`}
                aria-label={`Go to announcement ${index + 1}`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
